import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const portGrimaudEn: ChauffeurPage = {
  slug: "port-grimaud",
  title: "VTC & private chauffeur in Port Grimaud",
  subtitle:
    "Private chauffeur in Port Grimaud: the lakeside town of canals facing the gulf, Nice airport transfers and hourly hire.",
  keywords: [
    "vtc port grimaud",
    "private chauffeur port grimaud",
    "port grimaud airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Port Grimaud",
    subtitle:
      "The lakeside town of canals, facing the gulf: Nice airport transfer, hourly hire and long distance, 7 days a week.",
    keywords: [
      "VTC Port Grimaud",
      "lakeside town chauffeur",
      "Nice airport transfer",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "VTC & private chauffeur in Port Grimaud (lakeside town, Gulf of Saint-Tropez)",
    badges: [
      "Lakeside town & canals",
      "Nice airport transfer",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Port Grimaud",
    items: [
      {
        title: "Town entrance handled",
        description:
          "The lakeside heart is pedestrian, served by canals and the water bus: the chauffeur drops and collects at the entrance car parks, with no circling.",
      },
      {
        title: "On the edge of the gulf",
        description:
          "Moorings, quays and beaches a step from Saint-Tropez: a smooth link across the whole gulf.",
      },
      {
        title: "Long-distance transfer",
        description:
          "From Nice airport, around 1h20 to 1h30 of road handled in high-end comfort.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Port Grimaud",
    services: [
      {
        title: "Nice airport transfer",
        text: "Port Grimaud ⇄ Nice Côte d’Azur airport with flight tracking; a longer run than from Cannes, timed at departure.",
      },
      {
        title: "Saint-Raphaël TGV station",
        text: "Pick-up at Saint-Raphaël station, around 30 to 40 minutes away, as a rail alternative.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day for the canals, moorings, beaches and Saint-Tropez.",
      },
      {
        title: "Saint-Tropez & port link",
        text: "Port Grimaud ⇄ Saint-Tropez and its port, restaurants and nights out, with late returns.",
      },
      {
        title: "Crews & owners",
        text: "Transfers for crews, guests and owners from the moorings, coordinated with the concierge.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Lakeside town",
        description: "Colourful fishermen’s houses lining the canals.",
      },
      {
        name: "Saint-François-d’Assise church",
        description:
          "Modern church at the town’s heart, views from its bell tower.",
      },
      {
        name: "Water bus & pontoons",
        description: "River shuttle and private moorings between the quays.",
      },
      {
        name: "Port Grimaud beach",
        description: "Supervised beach at the entrance to the gulf.",
      },
      {
        name: "Saint-Raphaël station",
        description: "TGV station around 30-40 min away, a transfer point.",
      },
      {
        name: "Nice airport",
        description: "Nice Côte d’Azur airport, around 1h20-1h30 away.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur for Port Grimaud",
    subheading:
      "From the entrance of the lakeside town to the moorings, a premium service on the edge of the Gulf of Saint-Tropez.",
    paragraphs: [
      "Port Grimaud, the ‘Provençal Venice’, is laid out around its canals: colourful houses, private pontoons and a water bus serve an almost entirely pedestrian heart. You arrive at the entrance car parks, then continue on foot or by water; a chauffeur who drops and collects at the right access spares you many detours with luggage.",
      "Like the whole gulf, Port Grimaud has no airport: arrivals mostly come through Nice Côte d’Azur, around 1h20 to 1h30 away. We adjust the departure to the summer traffic, and offer Saint-Raphaël’s TGV station, some thirty minutes away, as an alternative transfer point.",
      "For mooring and villa guests, one chauffeur links the quays, the beaches, Grimaud and nearby Saint-Tropez, with discretion and flexible timing. Fixed fares agreed in advance or hourly hire: an immediate quote.",
    ],
    bullets: [
      "Drop-off at the town’s access points",
      "Nice airport & Saint-Raphaël station transfers",
      "Mooring & villa coordination",
    ],
  },
  process: {
    heading: "Book a chauffeur in Port Grimaud",
    steps: [
      "Tell us your access (entrance car park, mooring, quay) and times.",
      "We recommend the right vehicle and fare.",
      "The chauffeur tracks your flight or train and meets you at the right point.",
      "Direct transfer or hourly hire across the gulf during your stay.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Port Grimaud"),
    {
      q: "How long is the drive to Nice airport?",
      a: "Around 1h20 to 1h30 outside peak times, more in summer and during the gulf’s traffic peaks. We adjust departure and track your flight.",
    },
    {
      q: "Do you serve Saint-Raphaël TGV station?",
      a: "Yes, Saint-Raphaël station is around 30 to 40 minutes from Port Grimaud; a handy alternative transfer point for the train.",
    },
    {
      q: "Do you handle the link with Saint-Tropez?",
      a: "Yes, Saint-Tropez is very close; we run round trips to the port, restaurants and nights out, with late returns.",
    },
    {
      q: "How far ahead should I book?",
      a: "In high summer season, book as early as possible: demand is strong across the gulf. We also take last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to villa stays and an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes sedan, premium van (V-Class), SUV and minibus depending on passengers and luggage, suited to the long run from Nice or Cannes.",
    },
    {
      q: "Can you drive into the lakeside town?",
      a: "The heart of Port Grimaud is pedestrian and lined with canals. The chauffeur drops you at the entrance car park or the access nearest your mooring, then collects you at the same point.",
    },
  ],
  relatedLinks: [
    {
      label: "Car service in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private driver on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Short-term car rental in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Port Grimaud",
    text: "Nice airport transfer, Saint-Raphaël station or a night in Saint-Tropez: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Port Grimaud — private chauffeur & transfers",
    description:
      "Private car service in Port Grimaud: Nice airport transfer, Saint-Raphaël TGV station, hourly hire and the Saint-Tropez link.",
    canonical: "/en/chauffeur/port-grimaud",
  },
};

export default portGrimaudEn;
